import React, { FC, ReactNode, useState } from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';

import styles from './action-menu.module.scss';

import { VoidFunctionNoArgs } from 'types/common.interface';
import { Button } from 'views/components/UI/Button';
import { ThreeDots } from 'views/components/UI/Icons';
import { MenuList as Menu } from 'views/components/UI/Menu/MenuList';
import { Popover } from 'views/components/UI/Popover';

type ActionMenuItemTone = 'default' | 'danger';

interface IActionMenuItem {
  id: string;
  label: ReactNode;
  onSelect: VoidFunctionNoArgs;
  tone?: ActionMenuItemTone;
  disabled?: boolean;
  hidden?: boolean;
}

interface IActionMenuProps {
  items: IActionMenuItem[];
  triggerAriaLabel?: string;
  onOpen?: VoidFunctionNoArgs;
  menuWidth?: 'default' | 'wide';
}

let nextActionMenuId = 0;
const ITEM_NOT_FOUND = -1;

const ActionMenu: FC<IActionMenuProps> = props => {
  const { items, triggerAriaLabel, onOpen, menuWidth = 'default' } = props;
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuId] = useState(() => {
    nextActionMenuId += 1;
    return `action-menu-${nextActionMenuId}`;
  });

  const visibleItems = items.filter(item => !item.hidden);
  const isOpen = Boolean(anchorEl);
  const label =
    triggerAriaLabel ?? intl.formatMessage({ id: 'action-menu.btn.more-options' });
  const closeLabel = intl.formatMessage({ id: 'action-menu.btn.close' });

  const closeMenu = (restoreFocus = true): void => {
    const trigger = anchorEl;
    setAnchorEl(null);

    if (restoreFocus && trigger?.isConnected) {
      trigger.focus();
    }
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    onOpen?.();
    setAnchorEl(event.currentTarget);
  };

  const handleItemClick = (item: IActionMenuItem): void => {
    closeMenu(false);
    item.onSelect();
  };

  return (
    <>
      <Button
        aria-label={label}
        aria-hidden={isOpen || undefined}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        tabIndex={isOpen ? ITEM_NOT_FOUND : undefined}
        size="small"
        mode="tertiary"
        color="dark"
        icon={<ThreeDots />}
        onClick={handleTriggerClick}
      />
      {anchorEl && (
        <Popover anchorEl={anchorEl} handleClose={() => closeMenu()} placement="overlap">
          <div
            className={classNames(styles['action-menu'], {
              [styles.wide]: menuWidth === 'wide',
            })}
          >
            <Menu id={menuId} className={styles.menu} role="menu" aria-label={label}>
              {visibleItems.map(item => (
                <li key={item.id} role="none" className={styles.item}>
                  <button
                    type="button"
                    role="menuitem"
                    className={classNames(styles['item-button'], {
                      [styles.danger]: item.tone === 'danger',
                    })}
                    disabled={item.disabled}
                    onClick={() => handleItemClick(item)}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </Menu>
            <Button
              aria-label={closeLabel}
              size="small"
              mode="primary"
              color="accent"
              className={styles['active-trigger']}
              icon={<ThreeDots />}
              data-popover-overlap-anchor
              onClick={() => closeMenu()}
            />
          </div>
        </Popover>
      )}
    </>
  );
};

export { ActionMenu };
